import * as React from 'react'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import { AppState } from '../../store'
import CardEmptyInfo from '../../components/Card/CardEmptyInfo'
import { showConfirmModal, showOutboundMessageModal } from '../../store/modals/actions'
import { WidgetOutboundMessagesController } from '../../controllers'
import PageLoader from '../../components/Page/PageLoader'
import Badge from '../../components/Badge/Badge'
import { withTranslation, WithTranslation } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import ResourceTable, { ResourceTableAction } from '../../components/Resource/ResourceTable'
import ResourceTableRow from '../../components/Resource/ResourceTableRow'
import ResourceTableRowData from '../../components/Resource/ResourceTableRowData'
import ResourceTableRowActions from '../../components/Resource/ResourceTableRowActions'
import { CurrentUser, WidgetOutboundMessage, WidgetOutboundMessageFilter, WidgetOutboundMessageFilters, ResourceListFilterType } from '../../types'
import moment from '../../utilities/Moment'
import Notification from '../../utilities/Notification'
import ButtonPanel from '../../components/Button/ButtonPanel'
import ERoute from '../../ERoute'
import RouteHelper from '../../helpers/RouteHelper'

interface IOwnProps {
  widgetId: string
}

interface IStateToProps {
  currentUser: CurrentUser
}

interface IDispatchToProps {
  showConfirmModal: typeof showConfirmModal
  showOutboundMessageModal: typeof showOutboundMessageModal
}

type IProps = IOwnProps & IStateToProps & IDispatchToProps & RouteComponentProps<any> & WithTranslation

interface IState {
  outboundMessages: WidgetOutboundMessage[]
  page: number
  perPage: number
  totalPages: number
  didInitialLoad: boolean
  isFetching: boolean
  sortValue: string
  activeFilters: any
  selectedIds: string[]
  search: string
}

class WidgetOutboundMessageTable extends React.Component<IProps, IState> {
  constructor(props) {
    super(props)

    this.state = {
      outboundMessages: [],
      selectedIds: [],
      page: 1,
      perPage: 25,
      totalPages: 0,
      didInitialLoad: false,
      isFetching: false,
      sortValue: 'created_at_desc',
      activeFilters: {},
      search: '',
    }

    this.onTableActionClick = this.onTableActionClick.bind(this)
    this.onTableSelectionChange = this.onTableSelectionChange.bind(this)
    this.onTableRowSelectionChange = this.onTableRowSelectionChange.bind(this)
    this.onTableDeleteClick = this.onTableDeleteClick.bind(this)
    this.onNewMessageClick = this.onNewMessageClick.bind(this)
    this.onFiltersChange = this.onFiltersChange.bind(this)
    this.onSortValueChange = this.onSortValueChange.bind(this)
    this.onSearchChange = this.onSearchChange.bind(this)
    this.onSearchSubmit = this.onSearchSubmit.bind(this)
    this.onClearFilters = this.onClearFilters.bind(this)
    this.onBulkDeleteClick = this.onBulkDeleteClick.bind(this)
    this.onPageChange = this.onPageChange.bind(this)
    this.onPerPageChange = this.onPerPageChange.bind(this)
  }

  componentDidMount() {
    this.fetchOutboundMessages(1)
  }

  fetchOutboundMessages(page: number) {
    const { widgetId } = this.props
    const { search, sortValue, activeFilters, perPage } = this.state

    this.setState({ isFetching: true }, () => {
      WidgetOutboundMessagesController
        .getOutboundMessages(widgetId, { page, per_page: perPage, search, order: sortValue, ...activeFilters })
        .then(response => {
          const { outbound_messages, current_page, total_pages } = response

          this.setState({
            outboundMessages: [...(outbound_messages || [])],
            page: current_page || page,
            totalPages: total_pages || 0,
            didInitialLoad: true,
            isFetching: false,
          })
        })
        .catch(console.error)
    })
  }

  onPageChange(page: number) {
    this.setState({ page }, () => this.fetchOutboundMessages(page))
  }

  onPerPageChange(perPage: number) {
    this.setState({ page: 1, perPage }, () => this.fetchOutboundMessages(1))
  }

  onTableActionClick(key: string, outboundMessage: WidgetOutboundMessage) {
    switch (key) {
      case 'edit': this.onTableEditClick(outboundMessage)
        break
      case 'delete': this.onTableDeleteClick(outboundMessage)
        break
      default:
        throw Error('[WidgetOutboundMessageTable] Unimplemented onTableActionClick')
    }
  }

  onTableEditClick(outboundMessage: WidgetOutboundMessage) {
    const { widgetId, showOutboundMessageModal } = this.props

    showOutboundMessageModal({
      widgetId,
      outboundMessageId: outboundMessage.id,
      onSubmit: (updated) => {
        const { outboundMessages } = this.state
        const index = outboundMessages.findIndex(m => m.id === updated.id)
        if (index !== -1) {
          const next = [...outboundMessages]
          next[index] = updated
          this.setState({ outboundMessages: next })
        }
      },
    })
  }

  onTableSelectionChange(selectedIds: string[]) {
    this.setState({ selectedIds })
  }

  onTableRowSelectionChange(selected: boolean, id: string) {
    const { selectedIds } = this.state

    if (selected) {
      this.setState({ selectedIds: [...selectedIds, id] })
    } else {
      this.setState({ selectedIds: selectedIds.filter(selectedId => selectedId !== id) })
    }
  }

  onTableDeleteClick(outboundMessage: WidgetOutboundMessage) {
    const { showConfirmModal, t, widgetId } = this.props

    showConfirmModal({
      title: t('WidgetOutbound::Delete outbound message'),
      description: t('WidgetOutbound::You are about to delete this outbound message. Are you sure?'),
      action: { label: t('WidgetOutbound::Delete'), isDestructive: true },
      onConfirm: async () => {
        try {
          const response = await WidgetOutboundMessagesController.delete(widgetId, outboundMessage.id)

          if (response.errors) return

          const { outboundMessages, selectedIds } = this.state
          const index = outboundMessages.findIndex(c => c.id === outboundMessage.id)
          outboundMessages.splice(index, 1)

          this.setState({
            outboundMessages: [...outboundMessages],
            selectedIds: selectedIds.filter(id => id !== outboundMessage.id),
          })

          Notification.notifySuccess(t('WidgetOutbound::Outbound message successfully deleted'))
        } catch (ex) {
          console.error(ex)
        }
      }
    })
  }

  onFiltersChange(activeFilters: any) {
    this.setState({ page: 1, activeFilters }, () => this.fetchOutboundMessages(1))
  }

  onSortValueChange(value: string) {
    this.setState({ page: 1, sortValue: value }, () => this.fetchOutboundMessages(1))
  }

  onSearchChange(search: string) {
    this.setState({ search })
  }

  onSearchSubmit(search: string) {
    this.setState({ page: 1, search }, () => this.fetchOutboundMessages(1))
  }

  onClearFilters() {
    this.setState({ page: 1, search: '', activeFilters: {} }, () => this.fetchOutboundMessages(1))
  }

  onBulkDeleteClick() {
    const { t, widgetId } = this.props
    const { selectedIds, outboundMessages } = this.state

    this.props.showConfirmModal({
      title: t('WidgetOutbound::Bulk delete outbound messages'),
      description: t('WidgetOutbound::You are about to delete these outbound messages. Are you sure?'),
      action: { label: t('WidgetOutbound::Delete'), isDestructive: true },
      onConfirm: async () => {
        try {
          const response = await WidgetOutboundMessagesController.deleteAll(widgetId, selectedIds)

          if (response.errors) return

          const newOutboundMessages = outboundMessages.filter(c => !selectedIds.includes(c.id))
          this.setState({ outboundMessages: [...newOutboundMessages], selectedIds: [] })
          Notification.notifySuccess(t('WidgetOutbound::Outbound messages successfully deleted'))
        } catch (ex) {
          console.error(ex)
        }
      }
    })
  }

  onNewMessageClick() {
    const { widgetId, showOutboundMessageModal } = this.props

    showOutboundMessageModal({
      widgetId,
      onSubmit: (outboundMessage) => {
        const { outboundMessages } = this.state
        this.setState({ outboundMessages: [outboundMessage, ...outboundMessages] })
      },
    })
  }

  onRowClick(outboundMessage: WidgetOutboundMessage) {
    this.props.history.push(RouteHelper.process(ERoute.PATH_WIDGET_OUTBOUND_MESSAGE, { id: this.props.widgetId, outboundMessageId: outboundMessage.id }))
  }

  render() {
    const { t } = this.props
    const {
      outboundMessages,
      selectedIds,
      didInitialLoad,
      isFetching,
      activeFilters,
      sortValue,
      search,
      page,
      perPage,
      totalPages,
    } = this.state

    const filtersActive = search?.length > 0 || Object.keys(activeFilters).length > 0
    const filters: WidgetOutboundMessageFilter[] = [
      { name: 'name', label: t('WidgetOutbound::Name'), type: ResourceListFilterType.STRING },
      { name: 'type', label: t('WidgetOutbound::Type'), type: ResourceListFilterType.STRING },
      { name: 'created_at', label: t('WidgetOutbound::Created date'), type: ResourceListFilterType.DATE },
    ]
    const promotedBulkActions: ResourceTableAction[] = [
      { icon: 'trash-alt-solid', content: t('WidgetOutbound::Delete'), onAction: this.onBulkDeleteClick },
    ]

    if (!didInitialLoad) return <PageLoader />

    return (
      <ResourceTable
        data={outboundMessages}
        headers={[
          { title: t('WidgetOutbound::Name') },
          { title: t('WidgetOutbound::Type') },
          { title: t('WidgetOutbound::Created'), align: 'right' },
          { title: '', stickyRight: '0px' },
        ]}
        actionsLeft={[
          <ButtonPanel
            icon='plus'
            text={t('WidgetOutbound::New message')}
            onClick={this.onNewMessageClick}
          />
        ]}
        renderRow={(outboundMessage: WidgetOutboundMessage) => (
          <ResourceTableRow
            key={outboundMessage.id}
            selected={selectedIds.includes(outboundMessage.id)}
            onSelectionChange={(selected) => this.onTableRowSelectionChange(selected, outboundMessage.id)}
          >
            <ResourceTableRowData onClick={(): void => this.onRowClick(outboundMessage)}>
              {outboundMessage.name || t('WidgetOutbound::Untitled')}
            </ResourceTableRowData>
            <ResourceTableRowData onClick={(): void => this.onRowClick(outboundMessage)}>
              <Badge type='grey' text={outboundMessage.type} />
            </ResourceTableRowData>
            <ResourceTableRowData textAlign='right' onClick={(): void => this.onRowClick(outboundMessage)}>
              {outboundMessage.created_at ? moment(outboundMessage.created_at).format('LL') : '-'}
            </ResourceTableRowData>
            <ResourceTableRowActions
              actions={[
                { key: 'edit', icon: 'edit', content: t('WidgetOutbound::Edit') },
                { key: 'delete', icon: 'trash-alt-solid', content: t('WidgetOutbound::Delete'), destructive: true },
              ]}
              onActionClick={(key) => this.onTableActionClick(key, outboundMessage)}
              sticky={true}
              stickyRight='0px'
            />
          </ResourceTableRow>
        )}
        renderEmpty={<CardEmptyInfo
          icon={filtersActive ? 'search' : 'send'}
          description={filtersActive ? t('WidgetOutbound::No outbound messages found') : t('WidgetOutbound::No outbound messages have been added yet')}
          descriptionActionText={filtersActive ? t('WidgetOutbound::Clear filters') : undefined}
          onDescriptionActionClick={filtersActive ? this.onClearFilters : undefined}
        />}
        filters={filters}
        activeFilters={activeFilters}
        onFiltersChange={this.onFiltersChange}
        sortOptions={[
          { label: '-', value: '-' },
          { label: t('WidgetOutbound::Name (A-Z)'), value: 'name_asc' },
          { label: t('WidgetOutbound::Name (Z-A)'), value: 'name_desc' },
          { label: t('WidgetOutbound::Created at ↑'), value: 'created_at_asc' },
          { label: t('WidgetOutbound::Created at ↓'), value: 'created_at_desc' },
        ]}
        sortValue={sortValue}
        onSortChange={this.onSortValueChange}
        pagination={{ page, perPage, pageCount: totalPages }}
        onPageChange={this.onPageChange}
        onPerPageChange={this.onPerPageChange}
        isLoading={isFetching}
        stickyHeader={true}
        selectedItems={selectedIds}
        onSelectionChange={this.onTableSelectionChange}
        promotedBulkActions={promotedBulkActions}
        searchValue={search}
        onSearchChange={this.onSearchChange}
        onSearchSubmit={this.onSearchSubmit}
      />
    )
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const { authentication: { currentUser } } = state
  return { currentUser }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => ({
  showConfirmModal: (options) => dispatch(showConfirmModal(options)),
  showOutboundMessageModal: (options) => dispatch(showOutboundMessageModal(options)),
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(WidgetOutboundMessageTable)) as any) as any
